import type { HolidayEvent, PromotionTemplate } from '../types/index.js';
import { cleanPhoneForWhatsApp } from './clientUtils.js';

export const ALL_HOLIDAYS: HolidayEvent[] = [
  // =========================================================================
  // 🇺🇸 USA HOLIDAYS & COMMERCIAL EVENTS
  // =========================================================================
  {
    id: 'hol_usa_new_year',
    title: "New Year's Day",
    region: 'usa',
    region_label: 'USA 🇺🇸',
    date: '2026-01-01',
    month: 1,
    quarter: 'Q1',
    category: 'holiday',
    description: 'Start of the calendar year, ideal for annual digital strategy, platform refactoring, and tech stack refresh pitches.',
    suggested_offer: '20% off annual maintenance retainers or modern stack migration sprint.',
    templates_count: 2,
  },
  {
    id: 'hol_usa_valentine',
    title: "Valentine's Day",
    region: 'usa',
    region_label: 'USA 🇺🇸',
    date: '2026-02-14',
    month: 2,
    quarter: 'Q1',
    category: 'commercial',
    description: 'High-intent consumer retail and gifting holiday; perfect for customer delight UX reviews and conversion polish.',
    suggested_offer: '15% off UX/UI redesign, checkout optimization, and mobile polish.',
    templates_count: 1,
  },
  {
    id: 'hol_usa_presidents',
    title: "Presidents' Day",
    region: 'usa',
    region_label: 'USA 🇺🇸',
    date: '2026-02-16',
    month: 2,
    quarter: 'Q1',
    category: 'holiday',
    description: 'Federal holiday long weekend with significant retail surges and early Q1 marketing pushes.',
    suggested_offer: 'Fast-turnaround speed & performance boost package.',
    templates_count: 1,
  },
  {
    id: 'hol_usa_memorial',
    title: 'Memorial Day',
    region: 'usa',
    region_label: 'USA 🇺🇸',
    date: '2026-05-25',
    month: 5,
    quarter: 'Q2',
    category: 'holiday',
    description: 'Unofficial kick-off of summer; key milestone for launching mid-year features and summer promotional landing pages.',
    suggested_offer: 'Summer campaign feature sprint & landing page accelerator.',
    templates_count: 1,
  },
  {
    id: 'hol_usa_independence',
    title: 'Independence Day (4th July)',
    region: 'usa',
    region_label: 'USA 🇺🇸',
    date: '2026-07-04',
    month: 7,
    quarter: 'Q3',
    category: 'holiday',
    description: 'Mid-summer national holiday; prime window for scale-up architectural upgrades and cloud infrastructure cost audits.',
    suggested_offer: '25% off cloud infrastructure audit or SaaS refactor.',
    templates_count: 1,
  },
  {
    id: 'hol_usa_labor',
    title: 'Labor Day',
    region: 'usa',
    region_label: 'USA 🇺🇸',
    date: '2026-09-07',
    month: 9,
    quarter: 'Q3',
    category: 'holiday',
    description: 'Back-to-business milestone signaling the start of aggressive Q4 preparations and holiday season roadmaps.',
    suggested_offer: 'Q4 roadmap accelerator package & early technical prep.',
    templates_count: 1,
  },
  {
    id: 'hol_usa_halloween',
    title: 'Halloween',
    region: 'usa',
    region_label: 'USA 🇺🇸',
    date: '2026-10-31',
    month: 10,
    quarter: 'Q4',
    category: 'commercial',
    description: 'Spooky season commercial peak; prime time for "Don\'t let bugs haunt your store" audits and security checks.',
    suggested_offer: 'Ghost-bug extermination & pre-holiday security audit sprint.',
    templates_count: 2,
  },
  {
    id: 'hol_usa_thanksgiving',
    title: 'Thanksgiving',
    region: 'usa',
    region_label: 'USA 🇺🇸',
    date: '2026-11-26',
    month: 11,
    quarter: 'Q4',
    category: 'holiday',
    description: 'Major US family holiday ushering in the biggest digital commerce weekend of the entire calendar year.',
    suggested_offer: 'Pre-holiday e-commerce code freeze & VIP uptime assurance.',
    templates_count: 1,
  },
  {
    id: 'hol_usa_black_friday',
    title: 'Black Friday',
    region: 'usa',
    region_label: 'USA 🇺🇸',
    date: '2026-11-27',
    month: 11,
    quarter: 'Q4',
    category: 'commercial',
    description: 'The undisputed heavyweight shopping day on the planet; critical for e-commerce website revamps and conversion rate optimization.',
    suggested_offer: '30% off website overhaul or e-commerce conversion optimization.',
    templates_count: 3,
  },
  {
    id: 'hol_usa_cyber_monday',
    title: 'Cyber Monday',
    region: 'usa',
    region_label: 'USA 🇺🇸',
    date: '2026-11-30',
    month: 11,
    quarter: 'Q4',
    category: 'tech',
    description: 'Global online tech shopping extravaganza for SaaS tools, digital subscriptions, and annual tech retainers.',
    suggested_offer: 'Cyber week retainer discount or custom integration package.',
    templates_count: 2,
  },
  {
    id: 'hol_usa_christmas',
    title: 'Christmas',
    region: 'usa',
    region_label: 'USA 🇺🇸',
    date: '2026-12-25',
    month: 12,
    quarter: 'Q4',
    category: 'holiday',
    description: 'Year-end festive holiday; perfect for client appreciation goodwill gifts and locking in Q1 priority booking slots.',
    suggested_offer: 'Year-end bonus discount on next project booking.',
    templates_count: 1,
  },

  // =========================================================================
  // 🇪🇺 EUROPE (UK/EU) HOLIDAYS & COMMERCIAL EVENTS
  // =========================================================================
  {
    id: 'hol_eu_new_year',
    title: "New Year's Day (Europe)",
    region: 'europe',
    region_label: 'Europe 🇪🇺',
    date: '2026-01-01',
    month: 1,
    quarter: 'Q1',
    category: 'holiday',
    description: 'European public holiday and annual digital strategy renewal across the UK and European Union.',
    suggested_offer: 'New year tech architecture & GDPR compliance health check.',
    templates_count: 1,
  },
  {
    id: 'hol_eu_valentine',
    title: "Valentine's Day (Europe)",
    region: 'europe',
    region_label: 'Europe 🇪🇺',
    date: '2026-02-14',
    month: 2,
    quarter: 'Q1',
    category: 'commercial',
    description: 'Celebrated across UK and Continental Europe; great for e-commerce merchants and lifestyle platforms.',
    suggested_offer: '15% off conversion funnel redesign & checkout speedup.',
    templates_count: 1,
  },
  {
    id: 'hol_eu_easter',
    title: 'Easter Sunday & Monday',
    region: 'europe',
    region_label: 'Europe 🇪🇺',
    date: '2026-04-05',
    month: 4,
    quarter: 'Q2',
    category: 'holiday',
    description: 'Spring festive break; excellent timing for Q2 feature rollouts and mobile-first responsive redesigns.',
    suggested_offer: 'Spring feature release & speed optimization sprint.',
    templates_count: 1,
  },
  {
    id: 'hol_eu_spring_bank',
    title: 'Spring Bank Holiday',
    region: 'europe',
    region_label: 'Europe 🇪🇺',
    date: '2026-05-25',
    month: 5,
    quarter: 'Q2',
    category: 'holiday',
    description: 'UK & European late spring holiday break; mid-quarter checkpoint for digital roadmaps.',
    suggested_offer: 'Mid-quarter technical debt remediation & refactor package.',
    templates_count: 1,
  },
  {
    id: 'hol_eu_summer_bank',
    title: 'Summer Bank Holiday',
    region: 'europe',
    region_label: 'Europe 🇪🇺',
    date: '2026-08-31',
    month: 8,
    quarter: 'Q3',
    category: 'holiday',
    description: 'Late August UK bank holiday ahead of autumn commercial ramp-up and European tech budget resets.',
    suggested_offer: 'Autumn launch preparation sprint & team capacity reservation.',
    templates_count: 1,
  },
  {
    id: 'hol_eu_singles_day',
    title: "Singles' Day (11.11)",
    region: 'europe',
    region_label: 'Europe 🇪🇺',
    date: '2026-11-11',
    month: 11,
    quarter: 'Q4',
    category: 'commercial',
    description: 'The world\'s largest 24-hour online shopping festival, heavily adopted across European and UK cross-border e-commerce.',
    suggested_offer: 'Flash 48hr speed audit & checkout conversion sprint.',
    templates_count: 2,
  },
  {
    id: 'hol_eu_black_friday',
    title: 'Black Friday Europe',
    region: 'europe',
    region_label: 'Europe 🇪🇺',
    date: '2026-11-27',
    month: 11,
    quarter: 'Q4',
    category: 'commercial',
    description: 'Massive European e-commerce and digital commerce discounting event with surging conversion rates.',
    suggested_offer: '30% off website modernization or performance upgrade.',
    templates_count: 2,
  },
  {
    id: 'hol_eu_cyber_monday',
    title: 'Cyber Monday Europe',
    region: 'europe',
    region_label: 'Europe 🇪🇺',
    date: '2026-11-30',
    month: 11,
    quarter: 'Q4',
    category: 'tech',
    description: 'High-intent tech procurement and software upgrades across UK, Germany, Netherlands, and Scandinavia.',
    suggested_offer: 'Custom software feature build or API integration retainer.',
    templates_count: 2,
  },
  {
    id: 'hol_eu_christmas',
    title: 'Christmas Day (Europe)',
    region: 'europe',
    region_label: 'Europe 🇪🇺',
    date: '2026-12-25',
    month: 12,
    quarter: 'Q4',
    category: 'holiday',
    description: 'European festive holiday and winter downtime; ideal for warm client outreach and early Q1 retainer lock-in.',
    suggested_offer: 'Year-end partner thank you & Q1 early bird retainer.',
    templates_count: 1,
  },
  {
    id: 'hol_eu_boxing_day',
    title: 'Boxing Day',
    region: 'europe',
    region_label: 'Europe 🇪🇺',
    date: '2026-12-26',
    month: 12,
    quarter: 'Q4',
    category: 'commercial',
    description: 'Commonwealth traditional post-Christmas retail rush; massive opportunity for post-holiday inventory and clearance tech.',
    suggested_offer: 'Post-holiday retail optimization & clear-out sales sprint.',
    templates_count: 1,
  },

  // =========================================================================
  // 🇵🇰 PAKISTAN NATIONAL & RELIGIOUS HOLIDAYS
  // =========================================================================
  {
    id: 'hol_pak_kashmir',
    title: 'Kashmir Day',
    region: 'pakistan',
    region_label: 'Pakistan 🇵🇰',
    date: '2026-02-05',
    month: 2,
    quarter: 'Q1',
    category: 'holiday',
    description: 'National observance and public holiday across Pakistan, fostering nationwide solidarity.',
    suggested_offer: 'Special national holiday enterprise support package.',
    templates_count: 1,
  },
  {
    id: 'hol_pak_resolution',
    title: 'Pakistan Day (Youm-e-Pakistan)',
    region: 'pakistan',
    region_label: 'Pakistan 🇵🇰',
    date: '2026-03-23',
    month: 3,
    quarter: 'Q1',
    category: 'cultural',
    description: 'Commemorating the historic Lahore Resolution of 1940; a prime milestone for Pakistani tech startups and businesses.',
    suggested_offer: '23% off local enterprise digital transformation & mobile app sprint.',
    templates_count: 2,
  },
  {
    id: 'hol_pak_eid_fitr',
    title: 'Eid-ul-Fitr',
    region: 'pakistan',
    region_label: 'Pakistan 🇵🇰',
    date: '2026-03-20',
    month: 3,
    quarter: 'Q1',
    category: 'cultural',
    description: 'Joyous celebration concluding the holy month of Ramadan; prime for heartfelt client appreciation and festive vouchers.',
    suggested_offer: 'Eid Mubarak gift voucher: 25% off next project milestone.',
    templates_count: 2,
  },
  {
    id: 'hol_pak_labour',
    title: 'Labour Day (Pakistan)',
    region: 'pakistan',
    region_label: 'Pakistan 🇵🇰',
    date: '2026-05-01',
    month: 5,
    quarter: 'Q2',
    category: 'holiday',
    description: 'International Workers Day public holiday observed nationwide across Pakistan.',
    suggested_offer: 'Internal tooling automation sprint & team productivity boosters.',
    templates_count: 1,
  },
  {
    id: 'hol_pak_eid_adha',
    title: 'Eid-ul-Adha',
    region: 'pakistan',
    region_label: 'Pakistan 🇵🇰',
    date: '2026-05-27',
    month: 5,
    quarter: 'Q2',
    category: 'cultural',
    description: 'Feast of the Sacrifice (Bari Eid); auspicious occasion for partnership gratitude and mid-year planning.',
    suggested_offer: 'Bari Eid festive tech bundle & priority development queue.',
    templates_count: 2,
  },
  {
    id: 'hol_pak_ashura',
    title: 'Ashura (9-10 Muharram)',
    region: 'pakistan',
    region_label: 'Pakistan 🇵🇰',
    date: '2026-06-25',
    month: 6,
    quarter: 'Q2',
    category: 'cultural',
    description: 'Holy days of solemn reflection and nationwide public holidays.',
    suggested_offer: 'Proactive system maintenance and database backup audit.',
    templates_count: 1,
  },
  {
    id: 'hol_pak_independence',
    title: 'Independence Day (14th August)',
    region: 'pakistan',
    region_label: 'Pakistan 🇵🇰',
    date: '2026-08-14',
    month: 8,
    quarter: 'Q3',
    category: 'cultural',
    description: 'Pakistan Independence Day celebration; national wave of pride, startup grants, and patriotic discounts.',
    suggested_offer: 'Azaadi tech package: 14% discount on custom web application.',
    templates_count: 2,
  },
  {
    id: 'hol_pak_milad',
    title: 'Eid Milad-un-Nabi',
    region: 'pakistan',
    region_label: 'Pakistan 🇵🇰',
    date: '2026-08-25',
    month: 8,
    quarter: 'Q3',
    category: 'cultural',
    description: 'Observance of the Prophet\'s birthday (12 Rabi-ul-Awwal); auspicious religious public holiday.',
    suggested_offer: 'Blessing & goodwill client appreciation voucher.',
    templates_count: 1,
  },
  {
    id: 'hol_pak_quaid',
    title: 'Quaid-e-Azam Day',
    region: 'pakistan',
    region_label: 'Pakistan 🇵🇰',
    date: '2026-12-25',
    month: 12,
    quarter: 'Q4',
    category: 'cultural',
    description: 'Birthday of Father of the Nation Quaid-e-Azam Muhammad Ali Jinnah, observed alongside Christmas.',
    suggested_offer: 'Visionary leadership digital audit & year-end wrap up.',
    templates_count: 1,
  },

  // =========================================================================
  // 🌐 GLOBAL TECH / AGENCY OPPORTUNITIES
  // =========================================================================
  {
    id: 'hol_global_midyear_refresh',
    title: 'Mid-Year Website Refresh',
    region: 'global',
    region_label: 'Global 🌐',
    date: '2026-06-15',
    month: 6,
    quarter: 'Q2',
    category: 'tech',
    description: 'Strategic mid-year point to modernize aging agency websites, improve Core Web Vitals, and launch high-impact landing pages.',
    suggested_offer: '20% off comprehensive site redesign and performance boost.',
    templates_count: 2,
  },
  {
    id: 'hol_global_black_friday',
    title: 'Black Friday (Global Tech)',
    region: 'global',
    region_label: 'Global 🌐',
    date: '2026-11-27',
    month: 11,
    quarter: 'Q4',
    category: 'commercial',
    description: 'Worldwide commercial high watermark for digital products, Shopify stores, and SaaS applications.',
    suggested_offer: '30% off website overhaul or e-commerce conversion optimization.',
    templates_count: 3,
  },
  {
    id: 'hol_global_cyber_week',
    title: 'Cyber Week',
    region: 'global',
    region_label: 'Global 🌐',
    date: '2026-11-30',
    month: 11,
    quarter: 'Q4',
    category: 'tech',
    description: 'Week-long technology discount campaign focusing on software development and recurring maintenance.',
    suggested_offer: 'Cyber Week Retainer: Lock in monthly engineering hours at 25% off.',
    templates_count: 2,
  },
  {
    id: 'hol_global_eoy_retainer',
    title: 'End-of-Year Tech Retainer',
    region: 'global',
    region_label: 'Global 🌐',
    date: '2026-12-01',
    month: 12,
    quarter: 'Q4',
    category: 'tech',
    description: 'Q4 budget utilization sprint for enterprise clients looking to lock in developer capacity before fiscal year-end.',
    suggested_offer: 'Use-it-or-lose-it Q4 budget retainer with guaranteed priority SLAs.',
    templates_count: 2,
  },
];

export const PROMOTION_TEMPLATES: PromotionTemplate[] = [
  {
    id: 'tmpl_black_friday_revamp',
    holiday_id: 'hol_usa_black_friday',
    title: 'Black Friday 30% Off Revamp',
    category: 'commercial',
    subject: 'Black Friday Special: {discount_percent} Off Web Revamp for {company_name}',
    body: `Hi {client_name},

With Black Friday just around the corner, we're offering an exclusive {discount_percent} discount on full website redesigns, mobile speed optimization, and high-converting landing pages for {company_name}.

Whether you need to handle surge traffic, polish checkout flows, or roll out new features before {deadline}, our engineering team is ready to deliver within record turnaround times.

Would you like to hop on a quick 10-minute sync this week to explore what we can build together?

Best regards,
{sender_name}
Techosolution Agency`,
    suggested_discount: '30%',
    recommended_channel: 'all',
    target_services: ['Web Development', 'E-Commerce', 'Speed Optimization'],
  },
  {
    id: 'tmpl_cyber_monday_maintenance',
    holiday_id: 'hol_usa_cyber_monday',
    title: 'Cyber Monday Maintenance Plan',
    category: 'tech',
    subject: 'Cyber Monday Exclusive: {discount_percent} Off Monthly Maintenance for {company_name}',
    body: `Hi {client_name},

Cyber Monday is here! To ensure {company_name}'s digital infrastructure stays secure, blazingly fast, and 99.9% uptime guaranteed throughout the year, we're offering {discount_percent} off our dedicated monthly maintenance and support retainers.

This package includes proactive security updates, daily backups, 24/7 uptime monitoring, and priority feature tickets. Offer valid until {deadline}.

Let me know if you'd like to lock in this preferred agency rate!

Warmly,
{sender_name}
Techosolution Engineering`,
    suggested_discount: '25%',
    recommended_channel: 'email',
    target_services: ['Maintenance & Retainers', 'DevOps & Cloud', 'Bug Fixes'],
  },
  {
    id: 'tmpl_eid_fitr_gift',
    holiday_id: 'hol_pak_eid_fitr',
    title: 'Eid-ul-Fitr Special Gift',
    category: 'cultural',
    subject: 'Eid Mubarak from Techosolution! A Special Gift for {company_name}',
    body: `Eid Mubarak {client_name}! 🌙✨

Wishing you and your team at {company_name} peace, prosperity, and joy on this blessed occasion of Eid-ul-Fitr.

As a token of our appreciation for our partnership, we are delighted to extend a {discount_percent} festive voucher toward your next project milestone or software enhancement booked before {deadline}.

Thank you for trusting Techosolution. Enjoy the celebrations with your loved ones!

Warmest wishes,
{sender_name}
Techosolution Team`,
    suggested_discount: '25%',
    recommended_channel: 'whatsapp',
    target_services: ['Full-Stack Development', 'Custom Software', 'Mobile Apps'],
  },
  {
    id: 'tmpl_q4_cro_audit',
    holiday_id: 'hol_global_black_friday',
    title: 'Q4 E-Commerce Speed & CRO Audit',
    category: 'commercial',
    subject: "Boost {company_name}'s Q4 Sales: Comprehensive Speed & CRO Audit ({discount_percent} Off)",
    body: `Hello {client_name},

Every 100ms delay in website load time can cost up to 7% in lost sales during peak holiday season. We're launching our Q4 E-Commerce Speed & Conversion Rate Optimization (CRO) audit for {company_name} at an exclusive {discount_percent} discount.

We will analyze Core Web Vitals, checkout drop-off friction, and mobile responsiveness, providing actionable code fixes to maximize your Q4 revenue.

Shall we reserve an audit slot for your team before {deadline}?

Best,
{sender_name}
Techosolution Agency`,
    suggested_discount: '20%',
    recommended_channel: 'email',
    target_services: ['SEO & Audit', 'CRO Optimization', 'Web Development'],
  },
  {
    id: 'tmpl_pakistan_independence',
    holiday_id: 'hol_pak_independence',
    title: 'Independence Day Azaadi Booster',
    category: 'cultural',
    subject: '14th August Azaadi Offer: Special Tech Booster for {company_name}',
    body: `Happy Independence Day {client_name}! 🇵🇰

To celebrate Pakistan's Independence Day, we are proud to offer {company_name} an exclusive {discount_percent} discount on any custom web or mobile development project kicked off before {deadline}.

Let's build world-class digital products together that scale globally!

Best regards,
{sender_name}
Techosolution Team`,
    suggested_discount: '20%',
    recommended_channel: 'whatsapp',
    target_services: ['Web Development', 'Mobile Apps'],
  },
  {
    id: 'tmpl_new_year_architecture',
    holiday_id: 'hol_usa_new_year',
    title: 'New Year Tech Refresh & Architecture Audit',
    category: 'tech',
    subject: 'Start 2026 Strong: New Year Tech Stack Refresh for {company_name}',
    body: `Hi {client_name},

Happy New Year! As you plan your 2026 strategic objectives for {company_name}, our senior engineering team is offering a {discount_percent} discount on full architecture reviews, API integrations, and code modernization sprints.

Let's ensure your platform is built on modern, secure, and maintainable technology that outpaces the competition.

Book before {deadline} to claim priority onboarding for Q1.

Best,
{sender_name}
Techosolution OS`,
    suggested_discount: '20%',
    recommended_channel: 'email',
    target_services: ['Architecture Review', 'Custom Software'],
  },
  {
    id: 'tmpl_eoy_budget_retainer',
    holiday_id: 'hol_global_eoy_retainer',
    title: 'End-of-Year Tech Retainer & Budget Wrap',
    category: 'tech',
    subject: 'Q4 Budget Wrap-Up: Lock In Dedicated Developer Hours for {company_name}',
    body: `Hi {client_name},

If you have remaining Q4 technology or marketing budget at {company_name}, we can help you allocate it efficiently before year-end.

Lock in our senior engineering retainer at a guaranteed {discount_percent} discounted rate for the next 6 months. Hours can be used flexibly for new feature delivery, bug fixes, or performance tuning.

Offer closes on {deadline}. Let's chat!

Warm regards,
{sender_name}
Techosolution OS`,
    suggested_discount: '25%',
    recommended_channel: 'email',
    target_services: ['Retainers', 'Staff Augmentation'],
  },
  {
    id: 'tmpl_midyear_refresh',
    holiday_id: 'hol_global_midyear_refresh',
    title: 'Mid-Year Website Refresh Sprint',
    category: 'commercial',
    subject: 'Mid-Year Boost: Fresh Look & Blazing Speed for {company_name}',
    body: `Hi {client_name},

Summer is the ideal time to refresh {company_name}'s web presence before the busy autumn quarter.

Take advantage of our Mid-Year Refresh package with {discount_percent} off: updated hero sections, interactive animations, and mobile speed tuning.

Claim your sprint slot before {deadline}.

Cheers,
{sender_name}
Techosolution Agency`,
    suggested_discount: '20%',
    recommended_channel: 'all',
    target_services: ['UI/UX Redesign', 'Frontend Polish'],
  },
  {
    id: 'tmpl_singles_day_flash',
    holiday_id: 'hol_eu_singles_day',
    title: "Singles' Day 11.11 Flash Optimization",
    category: 'commercial',
    subject: '11.11 Flash Promotion: High-Speed Checkout Sprint for {company_name}',
    body: `Hi {client_name},

Singles' Day (11.11) is breaking global sales records! We're running a 48-hour flash offer: {discount_percent} off comprehensive checkout optimization and cart abandonment recovery workflows for {company_name}.

Valid until {deadline}. Let's get your store primed for conversion!

Best,
{sender_name}
Techosolution Team`,
    suggested_discount: '30%',
    recommended_channel: 'whatsapp',
    target_services: ['E-Commerce', 'CRO Optimization'],
  },
  {
    id: 'tmpl_halloween_bug_bash',
    holiday_id: 'hol_usa_halloween',
    title: 'Halloween Bug Bash & Security Audit',
    category: 'tech',
    subject: "Don't Let Bugs Spook {company_name}'s Customers: {discount_percent} Off Bug Bash",
    body: `Hi {client_name},

Spooky season is here, but software bugs shouldn't be terrifying your visitors! 🎃

Our team is offering a 2-day Bug Bash & Security vulnerability audit for {company_name} at {discount_percent} off. We'll identify and eliminate annoying UI glitches, broken links, and security vulnerabilities before the holiday rush.

Claim your slot before {deadline}!

Best regards,
{sender_name}
Techosolution Engineering`,
    suggested_discount: '25%',
    recommended_channel: 'email',
    target_services: ['QA & Testing', 'Security Audit', 'Bug Fixes'],
  },
];

/**
 * Calculates countdown details against a reference date (defaults to today midnight UTC).
 */
export function calculateCountdown(
  dateStr: string,
  referenceDate?: string | Date
): { daysUntil: number; badge: string; isPast: boolean; isToday: boolean } {
  if (!dateStr || typeof dateStr !== 'string') {
    return { daysUntil: 0, badge: '—', isPast: false, isToday: false };
  }
  const parts = dateStr.split('T')[0].split('-');
  if (parts.length < 3) {
    return { daysUntil: 0, badge: '—', isPast: false, isToday: false };
  }
  const targetYear = parseInt(parts[0], 10);
  const targetMonth = parseInt(parts[1], 10) - 1;
  const targetDay = parseInt(parts[2], 10);
  if (isNaN(targetYear) || isNaN(targetMonth) || isNaN(targetDay)) {
    return { daysUntil: 0, badge: '—', isPast: false, isToday: false };
  }
  const targetUtc = Date.UTC(targetYear, targetMonth, targetDay);

  let refUtc: number;
  if (referenceDate) {
    if (typeof referenceDate === 'string') {
      const refParts = referenceDate.split('T')[0].split('-');
      const y = parseInt(refParts[0], 10);
      const m = parseInt(refParts[1], 10) - 1;
      const d = parseInt(refParts[2], 10);
      refUtc = !isNaN(y) && !isNaN(m) && !isNaN(d) ? Date.UTC(y, m, d) : NaN;
    } else if (referenceDate instanceof Date && !isNaN(referenceDate.getTime())) {
      refUtc = Date.UTC(
        referenceDate.getUTCFullYear(),
        referenceDate.getUTCMonth(),
        referenceDate.getUTCDate()
      );
    } else {
      refUtc = NaN;
    }
  } else {
    refUtc = NaN;
  }

  if (isNaN(refUtc)) {
    const now = new Date();
    refUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  }

  const diffMs = targetUtc - refUtc;
  const daysUntil = Math.round(diffMs / 86400000);

  if (daysUntil === 0) {
    return { daysUntil, badge: 'Today! 🎉', isPast: false, isToday: true };
  }
  if (daysUntil === 1) {
    return { daysUntil, badge: 'Tomorrow', isPast: false, isToday: false };
  }
  if (daysUntil > 1 && daysUntil < 7) {
    return { daysUntil, badge: `In ${daysUntil} days`, isPast: false, isToday: false };
  }
  if (daysUntil >= 7 && daysUntil < 30) {
    const weeks = Math.round(daysUntil / 7);
    return {
      daysUntil,
      badge: `In ${weeks === 1 ? '1 week' : `${weeks} weeks`}`,
      isPast: false,
      isToday: false,
    };
  }
  if (daysUntil >= 30) {
    const months = Math.round(daysUntil / 30);
    return {
      daysUntil,
      badge: `In ${months <= 1 ? '1 month' : `${months} months`}`,
      isPast: false,
      isToday: false,
    };
  }
  if (daysUntil === -1) {
    return { daysUntil, badge: 'Yesterday', isPast: true, isToday: false };
  }
  return {
    daysUntil,
    badge: `Passed (${Math.abs(daysUntil)}d ago)`,
    isPast: true,
    isToday: false,
  };
}

export interface TemplateInterpolationVariables {
  clientName?: string;
  companyName?: string;
  discountPercent?: string;
  deadline?: string;
  senderName?: string;
  serviceName?: string;
}

/**
 * Interpolates variables into template subject and body.
 * Uses function replacement to safely handle values containing $, & or special replacement sequences.
 */
export function interpolateTemplate(
  template: { subject: string; body: string },
  vars: TemplateInterpolationVariables
): { subject: string; body: string } {
  const clientName = vars.clientName?.trim() || 'Partner';
  const companyName = vars.companyName?.trim() || 'your company';
  const discountPercent = vars.discountPercent?.trim() || '25%';
  const deadline = vars.deadline?.trim() || 'the upcoming holiday';
  const senderName = vars.senderName?.trim() || 'Techosolution Team';
  const serviceName = vars.serviceName?.trim() || 'Custom Software & E-Commerce';

  const replaceVars = (str: string) => {
    return str
      .replace(/\{client_name\}/gi, () => clientName)
      .replace(/\{company_name\}/gi, () => companyName)
      .replace(/\{discount_percent\}/gi, () => discountPercent)
      .replace(/\{deadline\}/gi, () => deadline)
      .replace(/\{sender_name\}/gi, () => senderName)
      .replace(/\{service_name\}/gi, () => serviceName);
  };

  return {
    subject: replaceVars(template.subject),
    body: replaceVars(template.body),
  };
}

/**
 * Formats a mailto: link with prefilled subject and body.
 * Normalizes newlines to CRLF (%0D%0A) for universal desktop/web mail client fidelity.
 */
export function buildEmailLink(email: string, subject: string, body: string): string {
  if (!email || !email.trim()) return '';
  const normalizedBody = body.replace(/\r?\n/g, '\r\n');
  return `mailto:${encodeURIComponent(email.trim())}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(normalizedBody)}`;
}

/**
 * Formats a WhatsApp link with sanitized international digits.
 */
export function buildWhatsAppLink(
  phone: string | null | undefined,
  body: string
): string | null {
  const digits = cleanPhoneForWhatsApp(phone);
  if (!digits) return null;
  return `https://wa.me/${digits}?text=${encodeURIComponent(body)}`;
}
